import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ExternalLink, X } from "lucide-react";

export const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;
axios.defaults.withCredentials = true;
export const SERVICE_NAMES = ["Silver", "Atlas Pro", "ES Pro", "Dino TV", "Iron Pro", "VIP TV", "King TV", "Fosto TV", "OTT TV"];
export const TICKET_ATTACHMENT_MAX_BYTES = 5 * 1024 * 1024;
export const TICKET_ATTACHMENT_ALLOWED_TYPES = ["image/jpeg", "image/png", "image/webp", "application/pdf"];

export const authHeaders = () => ({});

export const copyText = async (text, label) => {
  const value = text || "";
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(value);
    } else {
      throw new Error("Clipboard indisponible");
    }
    toast.success(`${label} copié`);
  } catch (error) {
    try {
      const textarea = document.createElement("textarea");
      textarea.value = value;
      textarea.setAttribute("readonly", "true");
      textarea.style.position = "fixed";
      textarea.style.opacity = "0";
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand("copy");
      document.body.removeChild(textarea);
      toast.success(`${label} copié`);
    } catch (fallbackError) {
      toast.error("Copie impossible. Sélectionnez le texte manuellement.");
    }
  }
};

export const getApiErrorMessage = (error, fallback = "Action impossible") => {
  if (error?.response?.status === 403) return "Action non autorisée.";
  const detail = error?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((item) => item?.msg || String(item)).join(" ");
  if (detail?.message) return detail.message;
  return fallback;
};

export const getActionErrorMessage = (error, fallback) => (error?.response?.status === 403 ? "Action non autorisée." : fallback);

export const cleanClientName = (name) => (name || "Client").replace(/\s+/g, " ").replace(/\s+([.,;:!?])/g, "$1").replace(/[\s.]+$/g, "").trim() || "Client";

export const titleCaseName = (value) => String(value || "")
  .replace(/\s+/g, " ")
  .trim()
  .toLocaleLowerCase("fr-FR")
  .replace(/(^|[\s'’-])([\p{L}])/gu, (match, separator, letter) => `${separator}${letter.toLocaleUpperCase("fr-FR")}`);

export const maskClientIdentifier = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return "";
  if (raw.includes("@")) {
    const [local, domain] = raw.split("@");
    const safeLocal = local.length <= 2 ? `${local[0] || ""}…` : `${local.slice(0, 2)}…${local.slice(-1)}`;
    return `${safeLocal}@${domain}`;
  }
  const digits = raw.replace(/\D/g, "");
  if (digits.length >= 6) return `${raw.slice(0, 4)}…${raw.slice(-2)}`;
  return raw.length > 4 ? `${raw.slice(0, 2)}…${raw.slice(-1)}` : raw;
};

export const formatClientDisplayName = (...sources) => {
  const merged = Object.assign({}, ...sources.filter(Boolean));
  const fullName = cleanClientName(merged.full_name || "");
  const splitName = cleanClientName(`${merged.first_name || ""} ${merged.last_name || ""}`);
  const firstName = cleanClientName(merged.first_name || "");
  const name = cleanClientName(merged.name || "");
  if (fullName && fullName !== "Client") return titleCaseName(fullName);
  if (splitName && splitName !== "Client") return titleCaseName(splitName);
  if (name && name !== "Client") return titleCaseName(name);
  if (firstName && firstName !== "Client") return titleCaseName(firstName);
  return maskClientIdentifier(merged.email || merged.contact_phone || merged.phone || merged.whatsapp) || "Client";
};

export const pluralizeFr = (count, singular, plural = `${singular}s`) => `${Number(count || 0)} ${Number(count || 0) > 1 ? plural : singular}`;

export const moneyValue = (value, currency = "EUR") => new Intl.NumberFormat("fr-FR", { style: "currency", currency }).format(Number(value || 0));

export const statusLabels = {
  nouveau: "Nouveau",
  en_cours: "En cours",
  en_attente: "En attente client",
  resolu: "Résolu",
  ferme: "Fermé",
  active: "Actif",
  verification: "Vérification",
  renouvellement: "Renouvellement",
  recu: "Reçu",
  en_validation: "En validation",
  pret: "Prêt",
  refuse: "Refusé",
  actif: "Actif",
  pause: "Désactivé",
  surveillance: "En attente",
  expire: "Expiré",
  suspendu: "Suspendu",
  test: "Test",
  en_verification: "En vérification",
  acceptee: "Acceptée",
  refusee: "Refusée",
};

export const announcementLevelLabels = {
  info: "Info",
  important: "Important",
  urgent: "Urgent",
  maintenance: "Maintenance",
  success: "Mise à jour",
  update: "Mise à jour",
  renewal: "Renouvellement",
};

export const badgeClass = (value) => {
  if (["critique", "refuse", "refusee", "expire", "suspendu", "urgent", "warning", "ferme"].includes(value)) return "badge badge-red";
  if (["haute", "en_validation", "en_attente", "en_verification", "verification", "renouvellement", "maintenance", "renewal", "test"].includes(value)) return "badge badge-amber";
  if (["active", "actif", "resolu", "pret", "acceptee", "confirme", "success", "update"].includes(value)) return "badge badge-green";
  if (["nouveau", "recu", "en_cours", "info"].includes(value)) return "badge badge-blue";
  if (SERVICE_NAMES.includes(value)) return "badge badge-gold";
  return "badge badge-neutral";
};

export const ticketWorkflowStatusLabel = (ticket) => {
  if (!ticket) return "—";
  if (["resolu", "ferme"].includes(ticket.status)) return statusLabels[ticket.status];
  if (ticket.waitingFor === "client") return "En attente client";
  if (ticket.waitingFor === "support") return ticket.status === "nouveau" ? "Nouveau" : "En attente support";
  return statusLabels[ticket.status] || ticket.status || "—";
};

export const formatDate = (value) => {
  if (!value) return "—";
  const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (match) return `${match[3]}/${match[2]}/${match[1]}`;
  return value;
};

export const formatDateTime = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (!Number.isNaN(date.getTime())) {
    return new Intl.DateTimeFormat("fr-FR", { dateStyle: "short", timeStyle: "short" }).format(date);
  }
  return formatDate(value);
};

export const isImageAttachment = (attachment) => {
  const type = attachment?.attachment_mime_type || "";
  const dataUrl = attachment?.attachment_data_url || "";
  return type.startsWith("image/") || dataUrl.startsWith("data:image/");
};

export const isPdfAttachment = (attachment) => {
  const type = attachment?.attachment_mime_type || "";
  const name = attachment?.attachment_name || "";
  return type === "application/pdf" || name.toLowerCase().endsWith(".pdf") || (attachment?.attachment_data_url || "").startsWith("data:application/pdf");
};

export const readTicketAttachment = (file) => new Promise((resolve, reject) => {
  if (!file) return resolve(null);
  if (!TICKET_ATTACHMENT_ALLOWED_TYPES.includes(file.type)) return reject(new Error("Format de fichier non autorisé."));
  if (file.size > TICKET_ATTACHMENT_MAX_BYTES) return reject(new Error("Le fichier dépasse la taille maximale de 5 MB."));
  const reader = new FileReader();
  reader.onerror = () => reject(new Error("Erreur lors de l’envoi du fichier."));
  reader.onload = () => resolve({
    attachment_data_url: reader.result,
    attachment_name: file.name,
    attachment_mime_type: file.type,
    attachment_size: file.size,
  });
  reader.readAsDataURL(file);
});

export const emptyTicketAttachment = { attachment_data_url: "", attachment_name: "", attachment_mime_type: "", attachment_size: 0 };

export const subscriptionStatusLabel = (status) => statusLabels[status] || status || "—";

export const validityText = (subscription) => {
  const expiration = subscription.expiration_date || subscription.renewal_date;
  if (!expiration) return "Validité non renseignée";
  const expired = ["expire", "suspendu"].includes(subscription.status) || new Date(`${expiration}T23:59:59`) < new Date();
  return expired ? `Expiré depuis le : ${formatDate(expiration)}` : `Valide jusqu’au : ${formatDate(expiration)}`;
};

export function PageHeader({ title, subtitle, icon: Icon, action }) {
  const isClientFacing = typeof window !== "undefined" && window.location.pathname.startsWith("/client");
  return (
    <div className="topbar" data-testid={`page-header-${title.toLowerCase().replaceAll(" ", "-")}`}>
      <div>
        <p className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.16em] text-[#d4af37]" data-testid="page-header-kicker">{Icon && <Icon size={16} />} IPTVISIONS</p>
        <h1 className="page-title" data-testid="page-title">{title}</h1>
        <p className="mt-2 text-sm text-slate-400" data-testid="page-subtitle">{subtitle}</p>
      </div>
      {action && <div data-testid="page-header-action">{action}</div>}
    </div>
  );
}

export function AccessDeliveryPanel({ access, clientId, onClose, onOpenProfile, onSendEmail }) {
  if (!access) return null;
  return (
    <div className="fixed inset-0 z-[90] grid place-items-center bg-black/70 p-4" data-testid="access-delivery-modal">
      <div className="surface panel-body max-h-[90vh] w-full max-w-3xl overflow-y-auto" data-testid="access-delivery-card">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="font-[Outfit] text-2xl text-white" data-testid="access-delivery-title">Accès client</h2>
            <p className="mt-2 text-sm text-slate-400" data-testid="access-delivery-status">{access.email_status_message}</p>
          </div>
          <button className="icon-btn" onClick={onClose} data-testid="access-delivery-close-button" aria-label="Fermer"><X size={18} /></button>
        </div>
        {access.email_warning && <p className="mt-4 rounded-md border border-amber-400/30 bg-amber-400/10 p-3 text-sm text-amber-100" data-testid="access-delivery-warning">{access.email_warning}</p>}
        <div className="mt-5 grid gap-3 text-sm text-slate-300 sm:grid-cols-2" data-testid="access-delivery-details">
          <p data-testid="access-delivery-login-link"><span className="text-slate-500">Lien :</span> {access.login_link}</p>
          <p data-testid="access-delivery-identifier"><span className="text-slate-500">Identifiant :</span> {access.login_identifier}</p>
          {access.temporary_password && <p data-testid="access-delivery-temp-password"><span className="text-slate-500">Mot de passe temporaire :</span> {access.temporary_password}</p>}
        </div>
        <div className="mt-5 grid gap-3 sm:grid-cols-2" data-testid="access-delivery-actions">
          <button className="btn-secondary" onClick={() => onSendEmail?.()} data-testid="access-delivery-send-email-button">Envoyer email d’accès</button>
          <button className="btn-secondary" onClick={() => copyText(access.whatsapp_message, "Message WhatsApp")} data-testid="access-delivery-copy-whatsapp-button">Copier message WhatsApp</button>
          <button className="btn-secondary" onClick={() => copyText(`Sujet: ${access.email_subject}\n\n${access.email_message}`, "Message email")} data-testid="access-delivery-copy-email-button">Copier message email</button>
          <button className="btn-primary" onClick={() => onOpenProfile?.(clientId)} data-testid="access-delivery-open-profile-button">Ouvrir profil client</button>
        </div>
      </div>
    </div>
  );
}

export function ConfirmationModal({ title, message, confirmLabel, cancelLabel = "Annuler", onConfirm, onCancel, loading = false, testId }) {
  return (
    <div className="fixed inset-0 z-[90] grid place-items-center bg-black/70 p-4" data-testid={`${testId}-modal`}>
      <div className="surface panel-body w-full max-w-xl" data-testid={`${testId}-card`}>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="font-[Outfit] text-2xl text-white" data-testid={`${testId}-title`}>{title}</h2>
            <p className="mt-3 text-sm text-slate-300" data-testid={`${testId}-message`}>{message}</p>
          </div>
          <button type="button" className="icon-btn" onClick={onCancel} data-testid={`${testId}-close-button`} aria-label="Fermer"><X size={18} /></button>
        </div>
        <div className="mt-6 flex flex-wrap justify-end gap-3" data-testid={`${testId}-actions`}>
          <button type="button" className="btn-secondary" onClick={onCancel} disabled={loading} data-testid={`${testId}-cancel-button`}>{cancelLabel}</button>
          <button type="button" className="btn-primary" onClick={onConfirm} disabled={loading} data-testid={`${testId}-confirm-button`}>{loading ? "Traitement…" : confirmLabel}</button>
        </div>
      </div>
    </div>
  );
}

export function useApiData(loader, deps = []) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reloadKey, setReloadKey] = useState(0);
  const loaderRef = useRef(loader);
  const depsKey = useMemo(() => JSON.stringify(deps), [deps]);

  useEffect(() => { loaderRef.current = loader; });

  useEffect(() => {
    let active = true;
    const run = async () => {
      setLoading(true);
      try {
        const result = await loaderRef.current();
        if (active) setData(result);
      } catch (error) {
        if (active) toast.error(error.response?.data?.detail || "Chargement impossible");
      } finally {
        if (active) setLoading(false);
      }
    };
    run();
    return () => { active = false; };
  }, [reloadKey, depsKey]);

  const reload = useCallback(() => setReloadKey((key) => key + 1), []);
  return { data, loading, reload };
}

export function TicketTable({ tickets = [], compact = false, onSelect, emptyMessage = "Aucun ticket à afficher." }) {
  const columnCount = compact ? 6 : 7;
  return <div className="table-wrap" data-testid="tickets-table-wrap"><table className="data-table" data-testid="tickets-table"><thead><tr><th>Sujet</th><th>Client</th><th>Service</th><th>Statut</th><th>Priorité</th><th>Escalade</th>{!compact && <th>Action</th>}</tr></thead><tbody>{tickets.length === 0 ? <tr data-testid="tickets-empty-row"><td colSpan={columnCount} data-testid="tickets-empty-cell"><div className="empty-state" data-testid="tickets-empty-state"><p className="font-[Outfit] text-lg text-white" data-testid="tickets-empty-title">Aucun ticket trouvé</p><p className="mt-1 text-sm text-slate-400" data-testid="tickets-empty-message">{emptyMessage}</p></div></td></tr> : tickets.map((ticket) => <tr key={ticket.id} data-testid={`ticket-row-${ticket.id}`}><td data-testid={`ticket-subject-${ticket.id}`}>{ticket.subject}</td><td data-testid={`ticket-client-${ticket.id}`}>{ticket.client_name}</td><td><span className={badgeClass(ticket.service_name)} data-testid={`ticket-service-${ticket.id}`}>{ticket.service_name}</span></td><td><span className={badgeClass(ticket.status)} data-testid={`ticket-status-${ticket.id}`}>{ticketWorkflowStatusLabel(ticket)}</span></td><td><span className={badgeClass(ticket.priority)} data-testid={`ticket-priority-${ticket.id}`}>{ticket.priority}</span></td><td data-testid={`ticket-escalation-${ticket.id}`}>{ticket.escalation ? "Oui" : "Non"}</td>{!compact && <td><button className="btn-secondary" onClick={() => onSelect(ticket)} data-testid={`ticket-open-button-${ticket.id}`}>Ouvrir</button></td>}</tr>)}</tbody></table></div>;
}

export function TicketAttachmentPreview({ note, compact = false, context = "conversation" }) {
  const [objectUrl, setObjectUrl] = useState(note?.attachment_data_url || "");
  const hasAttachment = Boolean(note?.attachment_data_url || note?.attachment_url);
  const image = isImageAttachment(note);
  const pdf = isPdfAttachment(note);

  useEffect(() => {
    let active = true;
    let createdUrl = "";
    if (note?.attachment_data_url) {
      setObjectUrl(note.attachment_data_url);
      return () => {};
    }
    if (!note?.attachment_url) {
      setObjectUrl("");
      return () => {};
    }
    const load = async () => {
      try {
        const { data } = await axios.get(`${BACKEND_URL}${note.attachment_url}`, { headers: authHeaders(), responseType: "blob" });
        createdUrl = URL.createObjectURL(data);
        if (active) setObjectUrl(createdUrl);
      } catch (error) {
        if (active) toast.error("Erreur lors de l’envoi du fichier.");
      }
    };
    load();
    return () => {
      active = false;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [note?.attachment_data_url, note?.attachment_url]);

  if (!hasAttachment) return null;
  const openAttachment = () => {
    if (objectUrl) window.open(objectUrl, "_blank", "noopener,noreferrer");
  };
  return (
    <div className="mt-3 rounded-md border border-white/10 bg-black/20 p-3" data-testid={`ticket-${context}-attachment-box-${note.id}`}>
      {image && objectUrl && <img className={`${compact ? "h-20" : "h-32"} w-full rounded-md object-contain bg-black/30`} src={objectUrl} alt={note.attachment_name || "Pièce jointe"} data-testid={`ticket-${context}-attachment-preview-${note.id}`} />}
      {pdf && <p className="text-sm text-slate-200" data-testid={`ticket-${context}-attachment-name-${note.id}`}>{note.attachment_name || "Document PDF"}</p>}
      {!image && !pdf && <p className="text-sm text-slate-200" data-testid={`ticket-${context}-attachment-name-${note.id}`}>{note.attachment_name || "Pièce jointe"}</p>}
      <button className="btn-secondary mt-3" type="button" onClick={openAttachment} disabled={!objectUrl} data-testid={`ticket-${context}-attachment-open-${note.id}`}>{pdf ? "Télécharger / ouvrir" : "Ouvrir"}</button>
    </div>
  );
}

export function TicketAttachmentInput({ value, onChange, inputTestId, previewTestId, label = "Joindre un fichier" }) {
  const [previewUrl, setPreviewUrl] = useState(value?.attachment_data_url || "");
  useEffect(() => {
    setPreviewUrl(value?.attachment_data_url || "");
  }, [value?.attachment_data_url]);
  const selectFile = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      const attachment = await readTicketAttachment(file);
      onChange(attachment || emptyTicketAttachment);
    } catch (error) {
      event.target.value = "";
      onChange(emptyTicketAttachment);
      toast.error(error.message || "Erreur lors de l’envoi du fichier.");
    }
  };
  const clearFile = () => {
    onChange(emptyTicketAttachment);
    setPreviewUrl("");
  };
  return (
    <div className="grid gap-2" data-testid={`${inputTestId}-group`}>
      <label className="text-xs uppercase tracking-wider text-slate-400" data-testid={`${inputTestId}-label`}>{label}</label>
      <div className="ticket-upload-control" data-testid={`${inputTestId}-control`}>
        <input id={inputTestId} className="sr-only" type="file" accept=".jpg,.jpeg,.png,.webp,.pdf,image/jpeg,image/png,image/webp,application/pdf" onChange={selectFile} data-testid={inputTestId} />
        <label className="btn-secondary ticket-upload-button" htmlFor={inputTestId} data-testid={`${inputTestId}-button`}>Choisir un fichier</label>
        <span className="ticket-upload-filename" data-testid={`${inputTestId}-filename`}>{value?.attachment_name || "Aucun fichier sélectionné"}</span>
      </div>
      {value?.attachment_name && <div className="rounded-md bg-[#1a1d27] p-3" data-testid={previewTestId}>
        {isImageAttachment(value) && previewUrl && <img className="h-28 w-full rounded-md object-contain bg-black/30" src={previewUrl} alt={value.attachment_name} data-testid={`${previewTestId}-image`} />}
        {isPdfAttachment(value) && <p className="text-sm text-slate-200" data-testid={`${previewTestId}-pdf-name`}>{value.attachment_name}</p>}
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <span className="text-xs text-slate-400" data-testid={`${previewTestId}-file-name`}>{value.attachment_name}</span>
          <button type="button" className="btn-ghost px-2 py-1" onClick={clearFile} data-testid={`${previewTestId}-remove-button`}>Retirer</button>
        </div>
      </div>}
    </div>
  );
}

export const ticketRoleLabel = { client: "Client", admin: "Admin", support: "Support" };

export function TicketConversationBubble({ note, context = "conversation" }) {
  if (!note) return null;
  const isClientMessage = note.author_role === "client";
  const isAutoReply = Boolean(note.is_auto_reply);
  const roleName = isAutoReply ? "Réponse automatique du support" : ticketRoleLabel[note.author_role] || note.author_role;
  const roleClass = isClientMessage ? "ticket-message-row-client" : "ticket-message-row-staff";
  const bubbleClass = isAutoReply ? "ticket-message-bubble ticket-message-bubble-auto" : isClientMessage ? "ticket-message-bubble ticket-message-bubble-client" : "ticket-message-bubble ticket-message-bubble-staff";
  const badgeClassName = isAutoReply ? "ticket-role-badge ticket-role-badge-auto" : isClientMessage ? "ticket-role-badge ticket-role-badge-client" : "ticket-role-badge ticket-role-badge-staff";
  return (
    <article className={`ticket-message-row ${roleClass}`} data-testid={`ticket-conversation-message-${note.id}`}>
      <div className={bubbleClass} data-testid={`ticket-message-bubble-${note.id}`}>
        <div className="ticket-message-meta" data-testid={`ticket-message-meta-${note.id}`}>
          <span className={badgeClassName} data-testid={`ticket-message-role-${note.id}`}>{roleName}</span>
          {isAutoReply && note.auto_rule_name && <span className="ticket-auto-rule-chip" data-testid={`ticket-auto-rule-${note.id}`}>Règle : {note.auto_rule_name}</span>}
          <span className="ticket-message-sender" data-testid={`ticket-message-sender-${note.id}`}>{note.author_name}</span>
          <span className="ticket-message-date" data-testid={`ticket-message-date-${note.id}`}>{formatDateTime(note.created_at)}</span>
        </div>
        <p className="ticket-message-content" data-testid={`ticket-message-content-${note.id}`}>{note.message}</p>
        <TicketAttachmentPreview note={note} context={context} />
      </div>
    </article>
  );
}

export function TicketInternalNoteBubble({ note }) {
  if (!note) return null;
  return (
    <article className="ticket-internal-note-bubble" data-testid={`ticket-internal-note-${note.id}`}>
      <div className="ticket-message-meta" data-testid={`ticket-internal-note-meta-row-${note.id}`}>
        <span className="ticket-role-badge ticket-role-badge-internal" data-testid={`ticket-internal-note-role-${note.id}`}>Note interne</span>
        <span className="ticket-message-sender" data-testid={`ticket-internal-note-sender-${note.id}`}>{note.author_name}</span>
        <span className="ticket-message-date" data-testid={`ticket-internal-note-date-${note.id}`}>{formatDateTime(note.created_at)}</span>
      </div>
      <p className="ticket-message-content" data-testid={`ticket-internal-note-message-${note.id}`}>{note.message}</p>
    </article>
  );
}

